import {
  createElement,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

type ActiveTenantContextValue = {
  activeTenantId: string | null;
  userTenantId: string | null;
  isAdmin: boolean;
  isImpersonating: boolean;
  setActiveTenantId: (tenantId: string | null) => Promise<void>;
  clearImpersonation: () => void;
  loading: boolean;
};

const ActiveTenantContext = createContext<ActiveTenantContextValue | undefined>(undefined);

const IMPERSONATION_EVENT = 'impersonation-change';
const TENANT_OVERRIDE_ROLES = new Set(['admin', 'admin-testing', 'manager']);

function readStoredTenantOverride(): string | null {
  try {
    const impersonation = localStorage.getItem('admin_impersonation');
    if (impersonation) {
      const parsed = JSON.parse(impersonation);
      if (parsed?.clientId) {
        return parsed.clientId;
      }
    }
  } catch (error) {
    console.error('Failed to parse impersonation data:', error);
    localStorage.removeItem('admin_impersonation');
  }

  return localStorage.getItem('impersonatedTenantId');
}

function clearStoredTenantOverride() {
  localStorage.removeItem('admin_impersonation');
  localStorage.removeItem('impersonatedTenantId');
}

async function validateStoredTenantOverride({
  role,
  tenantId,
  userId,
  userTenantId,
}: {
  role: string;
  tenantId: string | null;
  userId: string;
  userTenantId: string | null;
}): Promise<string | null> {
  if (!tenantId) return null;
  if (tenantId === userTenantId) return tenantId;

  const normalizedRole = String(role || '').toLowerCase();
  if (!TENANT_OVERRIDE_ROLES.has(normalizedRole)) {
    clearStoredTenantOverride();
    return null;
  }

  if (normalizedRole === 'admin' || normalizedRole === 'admin-testing') {
    const { data, error } = await supabase
      .from('clients')
      .select('id')
      .eq('id', tenantId)
      .maybeSingle();

    if (!error && data?.id) return tenantId;
    clearStoredTenantOverride();
    return null;
  }

  const { data, error } = await supabase
    .from('manager_access_grants')
    .select('id')
    .eq('manager_user_id', userId)
    .eq('target_client_id', tenantId)
    .eq('status', 'active')
    .maybeSingle();

  if (!error && data?.id) return tenantId;
  clearStoredTenantOverride();
  return null;
}

/**
 * Hook to manage active tenant context
 * This is the single source of truth for which tenant's data the UI/API operates on
 * 
 * Key concepts:
 * - Role: What the authenticated user is allowed to do (platform_admin, user)
 * - Active Tenant Context: Which tenant's data the UI/API operates on (activeTenantId)
 * 
 * Impersonation is NOT "switch auth user" - it is "admin sets active tenant context to another tenant"
 */
function useProvideActiveTenant(): ActiveTenantContextValue {
  const { user } = useAuth();
  const [activeTenantId, setActiveTenantIdState] = useState<string | null>(null);
  const [userTenantId, setUserTenantId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [role, setRole] = useState('user');

  // Load user's client_id directly from clients table (not from profiles.tenant_id)
  useEffect(() => {
    let cancelled = false;

    const loadUserClient = async () => {
      setLoading(true);
      setActiveTenantIdState(null);
      setUserTenantId(null);
      setIsAdmin(false);
      setRole('user');

      if (!user?.id) {
        if (!cancelled) {
          setLoading(false);
        }
        return;
      }

      try {
        // Get role from profile
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('role')
          .eq('id', user.id)
          .maybeSingle();

        if (profileError && profileError.code !== 'PGRST116') {
          console.error('Error loading profile:', profileError);
        }

        if (!cancelled) {
          const normalizedRole = String(profile?.role || 'user').toLowerCase();
          setRole(normalizedRole);
          setIsAdmin(normalizedRole === 'admin' || normalizedRole === 'admin-testing');
        }

        // Get client_id directly from clients table by user_id.
        // If duplicates exist, prefer the "real" account (active subscription / balance / stripe_customer_id), then fallback to oldest.
        const { data: clients, error: clientError } = await supabase
          .from('clients')
          .select('id, name, created_at, stripe_customer_id')
          .eq('user_id', user.id)
          .order('created_at', { ascending: true })
          .limit(50);

        if (clientError && clientError.code !== 'PGRST116') {
          console.error('Error loading clients:', clientError);
        }

        let clientId: string | null = null;
        if (clients && clients.length > 0) {
          if (clients.length === 1) {
            clientId = clients[0].id;
          } else {
            const clientIds = clients.map((c) => c.id).filter(Boolean);
            const now = new Date();

            let billingRows: any[] = [];
            let subscriptionRows: any[] = [];

            try {
              const [{ data: billingData, error: billingError }, { data: subsData, error: subsError }] = await Promise.all([
                supabase
                  .from('client_billing_settings')
                  .select('client_id, current_balance, stripe_customer_id, subscription_status, subscription_end_date')
                  .in('client_id', clientIds)
                  .limit(1000),
                supabase
                  .from('subscriptions')
                  .select('client_id, status, current_period_end, created_at')
                  .in('client_id', clientIds)
                  .order('current_period_end', { ascending: false })
                  .order('created_at', { ascending: false })
                  .limit(5000),
              ]);

              if (!billingError) billingRows = billingData || [];
              if (!subsError) subscriptionRows = subsData || [];
            } catch (e) {
              // Non-blocking: fall back to oldest client.
            }

            const billingByClientId = new Map<string, any>();
            billingRows.forEach((row) => {
              if (row?.client_id) billingByClientId.set(row.client_id, row);
            });

            const subByClientId = new Map<string, any>();
            subscriptionRows.forEach((row) => {
              if (row?.client_id && !subByClientId.has(row.client_id)) subByClientId.set(row.client_id, row);
            });

            const scoreClient = (c: any) => {
              let score = 0;
              const billing = billingByClientId.get(c.id) || null;
              const sub = subByClientId.get(c.id) || null;

              const subStatus = sub?.status || null;
              const subEnd = sub?.current_period_end ? new Date(sub.current_period_end) : null;
              const subEndInFuture = subEnd && subEnd > now;

              if (subStatus === 'active' || subStatus === 'trialing') score += 100;
              if (subStatus === 'past_due' && subEndInFuture) score += 80;

              const billingStatus = billing?.subscription_status || null;
              const billingEnd = billing?.subscription_end_date ? new Date(billing.subscription_end_date) : null;
              const billingEndInFuture = billingEnd && billingEnd > now;

              if (billingStatus === 'active' || billingStatus === 'trialing') score += 60;
              if (billingStatus === 'past_due' && billingEndInFuture) score += 40;

              const balance = Number(billing?.current_balance);
              if (Number.isFinite(balance) && balance > 0) score += 15;

              if (c?.stripe_customer_id || billing?.stripe_customer_id) score += 10;
              if (billing) score += 2;

              return score;
            };

            const sorted = [...clients].sort((a: any, b: any) => {
              const sA = scoreClient(a);
              const sB = scoreClient(b);
              if (sA !== sB) return sB - sA;
              const tA = a?.created_at ? new Date(a.created_at).getTime() : 0;
              const tB = b?.created_at ? new Date(b.created_at).getTime() : 0;
              return tA - tB;
            });

            clientId = sorted[0]?.id || clients[0].id;
          }
        } else {
          console.warn('No clients found for user:', user.id);
        }

        if (!cancelled) {
          setUserTenantId(clientId);
        }
      } catch (err) {
        console.error('Error loading user client:', err);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadUserClient();

    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  // Initialize activeTenantId on mount
  useEffect(() => {
    if (loading || !user?.id) return;

    const initializeTenant = async () => {
      // First, validate that userTenantId exists in clients table
      if (userTenantId) {
        const { data: clientCheck, error: clientCheckError } = await supabase
          .from('clients')
          .select('id')
          .eq('id', userTenantId)
          .maybeSingle();
        
        if (clientCheckError || !clientCheck) {
          console.warn('⚠️ userTenantId does not exist in clients table:', userTenantId);
          // Fallback: get client_id from clients table by user_id
          const { data: clients } = await supabase
            .from('clients')
            .select('id')
            .eq('user_id', user.id)
            .order('created_at', { ascending: true })
            .limit(1)
            .maybeSingle();
          
          if (clients?.id) {
            setUserTenantId(clients.id);
            setActiveTenantIdState(clients.id);
            return;
          }
        }
      }

      const storedTenantOverride = await validateStoredTenantOverride({
        role,
        tenantId: readStoredTenantOverride(),
        userId: user.id,
        userTenantId,
      });

      if (storedTenantOverride && storedTenantOverride !== userTenantId) {
        setActiveTenantIdState(storedTenantOverride);
        return;
      }

      setActiveTenantIdState(userTenantId);
    };

    initializeTenant();
  }, [loading, role, user?.id, userTenantId, isAdmin]);

  useEffect(() => {
    let cancelled = false;

    const syncTenantOverride = async () => {
      if (!user?.id) {
        setActiveTenantIdState(userTenantId);
        return;
      }

      const storedTenantOverride = await validateStoredTenantOverride({
        role,
        tenantId: readStoredTenantOverride(),
        userId: user.id,
        userTenantId,
      });

      if (!cancelled) {
        setActiveTenantIdState(storedTenantOverride || userTenantId);
      }
    };

    window.addEventListener('storage', syncTenantOverride);
    window.addEventListener(IMPERSONATION_EVENT, syncTenantOverride);

    return () => {
      cancelled = true;
      window.removeEventListener('storage', syncTenantOverride);
      window.removeEventListener(IMPERSONATION_EVENT, syncTenantOverride);
    };
  }, [role, user?.id, userTenantId]);

  // Set active tenant (for impersonation) - re-enabled for admin impersonation
  const setActiveTenantId = useCallback(async (tenantId: string | null) => {
    if (tenantId && tenantId !== userTenantId && user?.id) {
      const validTenantId = await validateStoredTenantOverride({
        role,
        tenantId,
        userId: user.id,
        userTenantId,
      });

      if (validTenantId) {
        localStorage.setItem('impersonatedTenantId', validTenantId);
        setActiveTenantIdState(validTenantId);
        window.dispatchEvent(new Event(IMPERSONATION_EVENT));
        return;
      }
    }

    clearStoredTenantOverride();
    setActiveTenantIdState(userTenantId);
    window.dispatchEvent(new Event(IMPERSONATION_EVENT));
  }, [role, user?.id, userTenantId]);

  // Clear impersonation
  const clearImpersonation = useCallback(() => {
    clearStoredTenantOverride();
    setActiveTenantIdState(userTenantId);
    window.dispatchEvent(new Event(IMPERSONATION_EVENT));
  }, [userTenantId]);

  // Compute view mode
  const isImpersonating = TENANT_OVERRIDE_ROLES.has(role) && !!activeTenantId && activeTenantId !== userTenantId;


  // F3: Clear cached data on tenant switch
  useEffect(() => {
    if (!activeTenantId) return;

    // Dispatch custom event to notify components of tenant switch
    // Components can listen to this and clear their caches
    const event = new CustomEvent('tenant-switch', { 
      detail: { tenantId: activeTenantId } 
    });
    window.dispatchEvent(event);
  }, [activeTenantId]);

  return useMemo(() => ({
    activeTenantId,
    userTenantId,
    isAdmin,
    isImpersonating,
    setActiveTenantId,
    clearImpersonation,
    loading,
  }), [activeTenantId, clearImpersonation, isAdmin, isImpersonating, loading, setActiveTenantId, userTenantId]);
}

export function ActiveTenantProvider({ children }: { children: ReactNode }) {
  const value = useProvideActiveTenant();
  return createElement(ActiveTenantContext.Provider, { value }, children);
}

export function useActiveTenant() {
  const context = useContext(ActiveTenantContext);
  if (!context) {
    throw new Error('useActiveTenant must be used within an ActiveTenantProvider');
  }
  return context;
}
